import React, { useState } from 'react';
import { PROGRESSIVE_HOME_OPTIONS as HOME } from './progressiveHomeOptions';

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}

const Field: React.FC<FieldProps> = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <label className="ca-run__field">
    <span>{label}</span>
    <input
      className="ca-run__input"
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
);

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}

const SelectField: React.FC<SelectFieldProps> = ({ label, value, onChange, options }) => (
  <label className="ca-run__field">
    <span>{label}</span>
    <select className="ca-run__select" value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </label>
);

interface QuoteFormProps {
  onSubmit: (payload: object) => void;
  disabled: boolean;
}

export const FalconQuoteForm: React.FC<QuoteFormProps> = ({ onSubmit, disabled }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [garagedZip, setGaragedZip] = useState('');
  const [vin, setVin] = useState('');

  return (
    <div className="ca-run__grid">
      <Field label="First Name" value={firstName} onChange={setFirstName} />
      <Field label="Last Name" value={lastName} onChange={setLastName} />
      <Field label="Date of Birth" value={dob} onChange={setDob} placeholder="MM/DD/YYYY" />
      <Field label="Garaged ZIP" value={garagedZip} onChange={setGaragedZip} />
      <Field label="VIN" value={vin} onChange={setVin} />
      <button
        type="button"
        className="ca-run__btn ca-run__btn--full"
        disabled={disabled}
        onClick={() => onSubmit({
          applicant: { firstName, lastName, dob, garagedZip },
          vehicle: { vin },
        })}
      >
        Create Quote
      </button>
    </div>
  );
};

export const HarborQuoteForm: React.FC<QuoteFormProps> = ({ onSubmit, disabled }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [garagingZip, setGaragingZip] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('Male');
  const [maritalStatus, setMaritalStatus] = useState('Single');
  const [licenseState, setLicenseState] = useState('');
  const [vin, setVin] = useState('');

  return (
    <div className="ca-run__grid">
      <Field label="First Name" value={firstName} onChange={setFirstName} />
      <Field label="Last Name" value={lastName} onChange={setLastName} />
      <Field label="Garaging ZIP" value={garagingZip} onChange={setGaragingZip} />
      <Field label="Date of Birth" value={dob} onChange={setDob} placeholder="YYYY-MM-DD" />
      <SelectField label="Gender" value={gender} onChange={setGender} options={['Male', 'Female']} />
      <SelectField label="Marital Status" value={maritalStatus} onChange={setMaritalStatus} options={['Single', 'Married', 'Separated', 'Divorced']} />
      <Field label="License State" value={licenseState} onChange={setLicenseState} placeholder="OK" />
      <Field label="VIN" value={vin} onChange={setVin} />
      <button
        type="button"
        className="ca-run__btn ca-run__btn--full"
        disabled={disabled}
        onClick={() => onSubmit({
          applicant: { firstName, lastName, garagingZip },
          driver: { dob, gender, maritalStatus, licenseState },
          vehicle: { vin },
        })}
      >
        Create Quote
      </button>
    </div>
  );
};

const COVERAGE_NONE = 'None';

export const AssuranceQuoteForm: React.FC<QuoteFormProps> = ({ onSubmit, disabled }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('Male');
  const [maritalStatus, setMaritalStatus] = useState('Single');
  const [mailingAddress, setMailingAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [priorCarrier, setPriorCarrier] = useState('');
  const [priorExpiration, setPriorExpiration] = useState('');
  const [priorMonths, setPriorMonths] = useState('');
  const [vin, setVin] = useState('');
  const [pullReports, setPullReports] = useState(false);

  const [spouseFirstName, setSpouseFirstName] = useState('');
  const [spouseLastName, setSpouseLastName] = useState('');
  const [spouseDob, setSpouseDob] = useState('');
  const [spouseGender, setSpouseGender] = useState('Male');

  const [medical, setMedical] = useState(COVERAGE_NONE);
  const [umbi, setUmbi] = useState(COVERAGE_NONE);
  const [comprehensive, setComprehensive] = useState(COVERAGE_NONE);
  const [collision, setCollision] = useState(COVERAGE_NONE);

  const isMarried = maritalStatus === 'Married';

  return (
    <div className="ca-run__grid">
      <Field label="First Name" value={firstName} onChange={setFirstName} />
      <Field label="Last Name" value={lastName} onChange={setLastName} />
      <Field label="Date of Birth" value={dob} onChange={setDob} placeholder="MM/DD/YYYY" />
      <SelectField label="Gender" value={gender} onChange={setGender} options={['Male', 'Female']} />
      <SelectField label="Marital Status" value={maritalStatus} onChange={setMaritalStatus} options={['Single', 'Married']} />
      <Field label="Mailing Address" value={mailingAddress} onChange={setMailingAddress} />
      <Field label="City" value={city} onChange={setCity} />
      <Field label="State" value={state} onChange={setState} placeholder="OK" />
      <Field label="ZIP" value={zip} onChange={setZip} />
      <Field label="Prior Carrier" value={priorCarrier} onChange={setPriorCarrier} />
      <Field label="Prior Expiration" value={priorExpiration} onChange={setPriorExpiration} placeholder="MM/DD/YYYY" />
      <Field label="Prior Months" value={priorMonths} onChange={setPriorMonths} />
      <Field label="VIN" value={vin} onChange={setVin} />

      {isMarried && (
        <>
          <Field label="Spouse First Name" value={spouseFirstName} onChange={setSpouseFirstName} />
          <Field label="Spouse Last Name" value={spouseLastName} onChange={setSpouseLastName} />
          <Field label="Spouse DOB" value={spouseDob} onChange={setSpouseDob} placeholder="MM/DD/YYYY" />
          <SelectField label="Spouse Gender" value={spouseGender} onChange={setSpouseGender} options={['Male', 'Female']} />
        </>
      )}

      <SelectField label="Medical Payments" value={medical} onChange={setMedical} options={['None', '500', '1,000', '2,000', '5,000']} />
      <SelectField label="Uninsured Motorist BI" value={umbi} onChange={setUmbi} options={['None', '25/50']} />
      <SelectField label="Comprehensive Deductible" value={comprehensive} onChange={setComprehensive} options={['None', '500', '1,000', '1,500']} />
      <SelectField label="Collision Deductible" value={collision} onChange={setCollision} options={['None', '500', '1,000', '1,500']} />

      <label className="ca-run__checkbox">
        <input type="checkbox" checked={pullReports} onChange={(e) => setPullReports(e.target.checked)} />
        Continue past the reports disclosure popup
      </label>

      <button
        type="button"
        className="ca-run__btn ca-run__btn--full"
        disabled={disabled}
        onClick={() => onSubmit({
          applicant: { firstName, lastName, dob, gender, maritalStatus, mailingAddress, city, state, zip, priorCarrier, priorExpiration, priorMonths },
          vehicle: { vin },
          pullReports,
          ...(isMarried ? { spouse: { firstName: spouseFirstName, lastName: spouseLastName, dob: spouseDob, gender: spouseGender } } : {}),
          coverage: { medical, umbi, comprehensive, collision },
        })}
      >
        Create Quote
      </button>
    </div>
  );
};

export const MercuryQuoteForm: React.FC<QuoteFormProps> = ({ onSubmit, disabled }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('OK');
  const [zip, setZip] = useState('');
  const [gender, setGender] = useState('M');
  const [maritalStatus, setMaritalStatus] = useState('S');
  const [vin, setVin] = useState('');
  const [spouseFirstName, setSpouseFirstName] = useState('');
  const [spouseLastName, setSpouseLastName] = useState('');
  const [spouseDob, setSpouseDob] = useState('');
  const [spouseGender, setSpouseGender] = useState('F');
  const isMarried = maritalStatus === 'M';

  return (
    <div className="ca-run__grid">
      <Field label="First Name" value={firstName} onChange={setFirstName} />
      <Field label="Last Name" value={lastName} onChange={setLastName} />
      <Field label="Date of Birth" value={dob} onChange={setDob} placeholder="MM/DD/YYYY" />
      <Field label="Address" value={addressLine1} onChange={setAddressLine1} />
      <Field label="City" value={city} onChange={setCity} />
      <Field label="State" value={state} onChange={setState} placeholder="OK" />
      <Field label="ZIP" value={zip} onChange={setZip} />
      <SelectField label="Gender" value={gender} onChange={setGender} options={['M', 'F']} />
      <SelectField label="Marital Status" value={maritalStatus} onChange={setMaritalStatus} options={['S', 'M', 'W', 'D', 'P', 'C']} />
      <Field label="VIN" value={vin} onChange={setVin} />
      {isMarried && (
        <>
          <Field label="Spouse First Name" value={spouseFirstName} onChange={setSpouseFirstName} />
          <Field label="Spouse Last Name" value={spouseLastName} onChange={setSpouseLastName} />
          <Field label="Spouse Date of Birth" value={spouseDob} onChange={setSpouseDob} placeholder="MM/DD/YYYY" />
          <SelectField label="Spouse Gender" value={spouseGender} onChange={setSpouseGender} options={['F', 'M']} />
        </>
      )}
      <button
        type="button"
        className="ca-run__btn ca-run__btn--full"
        disabled={disabled}
        onClick={() => onSubmit({
          applicant: { firstName, lastName, dob, addressLine1, city, state, zip },
          driver: { gender, maritalStatus },
          vehicle: { vin },
          ...(isMarried && spouseFirstName.trim() && spouseLastName.trim() && spouseDob.trim()
            ? { spouse: { firstName: spouseFirstName.trim(), lastName: spouseLastName.trim(), dob: spouseDob.trim(), gender: spouseGender } }
            : {}),
        })}
      >
        Create Quote
      </button>
    </div>
  );
};

export const ProgressiveQuoteForm: React.FC<QuoteFormProps> = ({ onSubmit, disabled }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('M');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [addressSearchText, setAddressSearchText] = useState('');
  const [maritalStatus, setMaritalStatus] = useState('S');
  const [employmentStatus, setEmploymentStatus] = useState('AK');
  const [occupation, setOccupation] = useState('AK5');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [year, setYear] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [bodyStyle, setBodyStyle] = useState('');
  const [vin, setVin] = useState('');
  const [garagingZip, setGaragingZip] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');

  return (
    <div className="ca-run__grid">
      <Field label="First Name" value={firstName} onChange={setFirstName} />
      <Field label="Last Name" value={lastName} onChange={setLastName} />
      <Field label="Date of Birth" value={dateOfBirth} onChange={setDateOfBirth} placeholder="MM/DD/YYYY" />
      <SelectField label="Gender" value={gender} onChange={setGender} options={['M', 'F']} />
      <Field label="Email" value={email} onChange={setEmail} placeholder="name@gmail.com" />
      <Field label="Phone" value={phoneNumber} onChange={setPhoneNumber} placeholder="10 digits" />
      <Field
        label="Address"
        value={addressSearchText}
        onChange={setAddressSearchText}
        placeholder="Street, City, State — enough to match a real suggestion"
      />
      <SelectField label="Marital Status" value={maritalStatus} onChange={setMaritalStatus} options={['S', 'M', 'P', 'W', 'D']} />
      <Field label="Employment Code" value={employmentStatus} onChange={setEmploymentStatus} placeholder="e.g. AK = IT, 02 = Retired" />
      <Field label="Occupation Code" value={occupation} onChange={setOccupation} placeholder="e.g. AK5 = Engineer - Software" />
      <Field label="License Number" value={licenseNumber} onChange={setLicenseNumber} />
      <Field label="Vehicle Year" value={year} onChange={setYear} placeholder="2015" />
      <Field label="Make" value={make} onChange={setMake} placeholder="HONDA (uppercase)" />
      <Field label="Model" value={model} onChange={setModel} placeholder="ACCORD (uppercase)" />
      <Field label="Body Style" value={bodyStyle} onChange={setBodyStyle} placeholder="4DR 4CYL" />
      <Field label="VIN" value={vin} onChange={setVin} />
      <Field label="Garaging ZIP" value={garagingZip} onChange={setGaragingZip} />
      <Field label="Vehicle Purchase Date" value={purchaseDate} onChange={setPurchaseDate} placeholder="MM/DD/YYYY" />
      <button
        type="button"
        className="ca-run__btn ca-run__btn--full"
        disabled={disabled}
        onClick={() => onSubmit({
          applicant: {
            firstName, lastName, dateOfBirth, gender, email, phoneNumber, addressSearchText,
            maritalStatus, employmentStatus, occupation, licenseNumber,
          },
          vehicle: { year, make, model, bodyStyle, vin, garagingZip, purchaseDate },
        })}
      >
        Create Quote
      </button>
    </div>
  );
};

export const NationalGeneralQuoteForm: React.FC<QuoteFormProps> = ({ onSubmit, disabled }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [mailingAddress, setMailingAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('Texas');
  const [zip, setZip] = useState('');
  const [gender, setGender] = useState('Male');
  const [maritalStatus, setMaritalStatus] = useState('Single');
  const [yearsExperience, setYearsExperience] = useState('');
  const [licenseState, setLicenseState] = useState('TX');
  const [modelYear, setModelYear] = useState('');
  const [make, setMake] = useState('');
  const [garagingZip, setGaragingZip] = useState('');
  const [garagingState, setGaragingState] = useState('TX');

  return (
    <div className="ca-run__grid">
      <Field label="First Name" value={firstName} onChange={setFirstName} />
      <Field label="Last Name" value={lastName} onChange={setLastName} />
      <Field label="Date of Birth" value={birthDate} onChange={setBirthDate} placeholder="MM/DD/YYYY" />
      <Field label="Effective Date" value={effectiveDate} onChange={setEffectiveDate} placeholder="MM/DD/YYYY" />
      <Field label="Mailing Address" value={mailingAddress} onChange={setMailingAddress} />
      <Field label="City" value={city} onChange={setCity} />
      <Field label="State" value={state} onChange={setState} placeholder="Texas (full name)" />
      <Field label="ZIP" value={zip} onChange={setZip} />
      <SelectField label="Gender" value={gender} onChange={setGender} options={['Male', 'Female']} />
      <SelectField label="Marital Status" value={maritalStatus} onChange={setMaritalStatus} options={['Single', 'Married', 'Divorced', 'Widowed', 'Separated']} />
      <Field label="Years Licensed" value={yearsExperience} onChange={setYearsExperience} placeholder="e.g. 10" />
      <Field label="Driver License State" value={licenseState} onChange={setLicenseState} placeholder="TX (abbreviation)" />
      <Field label="Vehicle Model Year" value={modelYear} onChange={setModelYear} placeholder="2020" />
      <Field label="Vehicle Make" value={make} onChange={setMake} placeholder="TOYOTA (uppercase)" />
      <Field label="Garaging ZIP" value={garagingZip} onChange={setGaragingZip} />
      <Field label="Garaging State" value={garagingState} onChange={setGaragingState} placeholder="TX (abbreviation)" />
      <button
        type="button"
        className="ca-run__btn ca-run__btn--full"
        disabled={disabled}
        onClick={() => onSubmit({
          applicant: { firstName, lastName, birthDate, effectiveDate, mailingAddress, city, state, zip },
          driver: { gender, maritalStatus, yearsExperience: Number(yearsExperience), licenseState },
          vehicle: { modelYear, make, garagingZip, garagingState },
        })}
      >
        Create Quote
      </button>
    </div>
  );
};

export const LibertyMutualQuoteForm: React.FC<QuoteFormProps> = ({ onSubmit, disabled }) => {
  const [ratingState, setRatingState] = useState('Oklahoma');
  const [agentNumber, setAgentNumber] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [mailingAddress, setMailingAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('OK');
  const [zip, setZip] = useState('');
  const [maritalStatus, setMaritalStatus] = useState('Single');
  const [gender, setGender] = useState('Male');
  const [licenseState, setLicenseState] = useState('OK');
  const [ageFirstLicensed, setAgeFirstLicensed] = useState('16');
  const [modelYear, setModelYear] = useState('');
  const [make, setMake] = useState('');

  return (
    <div className="ca-run__grid">
      <Field label="Rating State" value={ratingState} onChange={setRatingState} placeholder="Oklahoma (full name)" />
      <Field label="Agent Number" value={agentNumber} onChange={setAgentNumber} placeholder="e.g. 32-0796" />
      <Field label="First Name" value={firstName} onChange={setFirstName} />
      <Field label="Last Name" value={lastName} onChange={setLastName} />
      <Field label="Date of Birth" value={birthDate} onChange={setBirthDate} placeholder="MM/DD/YYYY" />
      <Field label="Effective Date" value={effectiveDate} onChange={setEffectiveDate} placeholder="MM/DD/YYYY" />
      <Field label="Mailing Address" value={mailingAddress} onChange={setMailingAddress} />
      <Field label="City" value={city} onChange={setCity} />
      <Field label="State" value={state} onChange={setState} placeholder="OK (abbreviation)" />
      <Field label="ZIP" value={zip} onChange={setZip} />
      <SelectField label="Marital Status" value={maritalStatus} onChange={setMaritalStatus} options={['Single', 'Married', 'Divorced', 'Separated', 'Widowed']} />
      <SelectField label="Gender" value={gender} onChange={setGender} options={['Male', 'Female']} />
      <Field label="Driver License State" value={licenseState} onChange={setLicenseState} placeholder="OK (abbreviation)" />
      <Field label="Age First Licensed" value={ageFirstLicensed} onChange={setAgeFirstLicensed} placeholder="16" />
      <Field label="Vehicle Model Year" value={modelYear} onChange={setModelYear} placeholder="2020" />
      <Field label="Vehicle Make" value={make} onChange={setMake} placeholder="Toyota" />
      <button
        type="button"
        className="ca-run__btn ca-run__btn--full"
        disabled={disabled}
        onClick={() => onSubmit({
          ratingState,
          agentNumber,
          applicant: { firstName, lastName, birthDate, effectiveDate, mailingAddress, city, state, zip, maritalStatus },
          driver: { gender, licenseState, ageFirstLicensed: Number(ageFirstLicensed) },
          vehicle: { modelYear, make },
        })}
      >
        Create Quote
      </button>
    </div>
  );
};

// ─── Added from Kieran's carrier-browser package (2026-10-12). Additive only. ───

const HOME_INELIGIBLE_CONDITIONS =
  'prior liability claim; business activity at the location; underground fuel tank; ineligible dog breed; more than one roof overlay; ' +
  'sinkhole, settlement or cracking damage; ineligible electrical or plumbing system; dwelling under construction; unfenced pool or hot tub; ' +
  'unrepaired damage; resident employees; home day care; 2 or more claims in the last 5 years; insurance canceled, non-renewed or declined';

/**
 * Progressive Homeowners (HO3). The two checkboxes are the agent's own
 * statements to the carrier — Progressive will not quote without them, so
 * neither is ever pre-ticked and Create Quote stays disabled until both
 * are. See automation/lib/progressiveHomeQuote.ts.
 */
export const TravelersQuoteForm: React.FC<QuoteFormProps> = ({ onSubmit, disabled }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('M');
  const [maritalStatus, setMaritalStatus] = useState('Single');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [agentCode, setAgentCode] = useState('');
  const [county, setCounty] = useState('');
  const [assumedScore, setAssumedScore] = useState('Fair');
  const [year, setYear] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [bodyStyle, setBodyStyle] = useState('');
  const [use, setUse] = useState('Pleasure Use');
  const [annualMileage, setAnnualMileage] = useState('8000');
  const [ageFirstLicensed, setAgeFirstLicensed] = useState('16');
  const [dateLicensed, setDateLicensed] = useState('');
  const [insuranceStatus, setInsuranceStatus] = useState('Currently Insured');
  const [currentCarrier, setCurrentCarrier] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [bodilyInjuryLimits, setBodilyInjuryLimits] = useState('30/60 (CSL 85)');
  const [lengthWithCurrentCompany, setLengthWithCurrentCompany] = useState('Greater than or equal to 5 years');
  const [continuousInsurance, setContinuousInsurance] = useState('Greater than or equal to 5 years');
  const [primaryResidence, setPrimaryResidence] = useState('Rent/Other');
  const [liability, setLiability] = useState('100/300');
  const [propertyDamage, setPropertyDamage] = useState('100');
  const [uninsuredMotorist, setUninsuredMotorist] = useState('100/300');
  const [comprehensiveDeductible, setComprehensiveDeductible] = useState('500');
  const [collisionDeductible, setCollisionDeductible] = useState('500');

  return (
    <div className="ca-run__grid">
      <Field label="First Name" value={firstName} onChange={setFirstName} />
      <Field label="Last Name" value={lastName} onChange={setLastName} />
      <Field label="Street" value={street} onChange={setStreet} />
      <Field label="City" value={city} onChange={setCity} />
      <Field label="ZIP" value={zip} onChange={setZip} />
      <Field label="Phone" value={phone} onChange={setPhone} placeholder="10 digits (a real-looking number)" />
      <Field label="Date of Birth" value={dateOfBirth} onChange={setDateOfBirth} placeholder="MM/DD/YYYY" />
      <SelectField label="Gender" value={gender} onChange={setGender} options={['M', 'F']} />
      <SelectField label="Marital Status" value={maritalStatus} onChange={setMaritalStatus} options={['Single', 'Married']} />
      <Field label="Effective Date" value={effectiveDate} onChange={setEffectiveDate} placeholder="MM/DD/YYYY" />
      <Field label="Agent Code" value={agentCode} onChange={setAgentCode} placeholder="Your Travelers producer code" />
      <Field label="County" value={county} onChange={setCounty} placeholder="As Travelers lists it, e.g. WOOD" />
      <SelectField label="Assumed Score" value={assumedScore} onChange={setAssumedScore} options={['Excellent', 'Good', 'Fair', 'Poor', 'No Hit']} />
      <Field label="Vehicle Year" value={year} onChange={setYear} placeholder="2015" />
      <Field label="Make" value={make} onChange={setMake} placeholder="HONDA (uppercase)" />
      <Field label="Model" value={model} onChange={setModel} placeholder="ACCORD LX (as Travelers lists it)" />
      <Field label="Body Style" value={bodyStyle} onChange={setBodyStyle} placeholder="Leave blank to take the first one" />
      <SelectField label="Vehicle Use" value={use} onChange={setUse} options={['Pleasure Use', 'Farm Use', 'Business Use', 'Drive to work / school']} />
      <Field label="Annual Mileage" value={annualMileage} onChange={setAnnualMileage} />
      <Field label="Age First Licensed" value={ageFirstLicensed} onChange={setAgeFirstLicensed} />
      <Field label="Date Licensed" value={dateLicensed} onChange={setDateLicensed} placeholder="MM/DD/YYYY" />
      <SelectField label="Insurance Status" value={insuranceStatus} onChange={setInsuranceStatus} options={['Currently Insured', 'Current Lapse 30 Days or Less', 'Current Lapse greater than 30 Days', 'No Prior Insurance']} />
      <Field label="Current Carrier" value={currentCarrier} onChange={setCurrentCarrier} placeholder="As Travelers lists it (if currently insured)" />
      <Field label="Policy Expiration Date" value={expirationDate} onChange={setExpirationDate} placeholder="MM/DD/YYYY (if currently insured)" />
      <Field label="Current Bodily Injury Limits" value={bodilyInjuryLimits} onChange={setBodilyInjuryLimits} />
      <Field label="Length With Current Company" value={lengthWithCurrentCompany} onChange={setLengthWithCurrentCompany} />
      <Field label="Continuous Insurance" value={continuousInsurance} onChange={setContinuousInsurance} />
      <SelectField label="Primary Residence" value={primaryResidence} onChange={setPrimaryResidence} options={['Home', 'Condo', 'Rent/Other', 'Mobile Homeowner']} />
      <Field label="Liability" value={liability} onChange={setLiability} />
      <Field label="Property Damage" value={propertyDamage} onChange={setPropertyDamage} />
      <Field label="Uninsured Motorist" value={uninsuredMotorist} onChange={setUninsuredMotorist} />
      <Field label="Comprehensive Deductible" value={comprehensiveDeductible} onChange={setComprehensiveDeductible} />
      <Field label="Collision Deductible" value={collisionDeductible} onChange={setCollisionDeductible} />
      <button
        type="button"
        className="ca-run__btn ca-run__btn--full"
        disabled={disabled}
        onClick={() => onSubmit({
          applicant: { firstName, lastName, street, city, zip, phone, dateOfBirth, gender, maritalStatus },
          policy: { effectiveDate, agentCode, county },
          assumedScore,
          vehicle: { year, make, model, bodyStyle: bodyStyle || undefined, use, annualMileage },
          driver: { ageFirstLicensed, dateLicensed },
          underwriting: {
            insuranceStatus, currentCarrier, expirationDate, bodilyInjuryLimits, lengthWithCurrentCompany, continuousInsurance,
            vehiclesNotRegisteredToInsured: false, driverWithoutValidLicense: false,
            declinedOrCancelledInLast3Years: false, militaryDeployedInLast5Years: false, primaryResidence,
          },
          coverage: { liability, propertyDamage, uninsuredMotorist, comprehensiveDeductible, collisionDeductible },
        })}
      >
        Create Quote
      </button>
    </div>
  );
};

export const OpenlyQuoteForm: React.FC<QuoteFormProps> = ({ onSubmit, disabled }) => {
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [propertyType, setPropertyType] = useState('Main Residence');
  const [address, setAddress] = useState('');
  const [disclosureRead, setDisclosureRead] = useState(false);
  const [roofYearBuilt, setRoofYearBuilt] = useState('');
  const [squareFootage, setSquareFootage] = useState('');
  const [yearBuilt, setYearBuilt] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [stories, setStories] = useState('1');
  const [foundationType, setFoundationType] = useState('');
  const [buildingMaterial, setBuildingMaterial] = useState('');
  const [singleFamilyHome, setSingleFamilyHome] = useState('');
  const [unconventionalHomeType, setUnconventionalHomeType] = useState('');
  const [cancellationStatus, setCancellationStatus] = useState('');
  const [homeInsuranceStatus, setHomeInsuranceStatus] = useState('');
  const [hasGarage, setHasGarage] = useState('No');
  const [hasPool, setHasPool] = useState('No');
  const [hasSolarPanels, setHasSolarPanels] = useState('No');
  const [hasDogs, setHasDogs] = useState('No');
  const [stoveHeating, setStoveHeating] = useState('No');
  const [onSiteBusiness, setOnSiteBusiness] = useState('No');
  const [ruralFireDues, setRuralFireDues] = useState('No');
  const [otherStructures, setOtherStructures] = useState('');
  const [personalProperty, setPersonalProperty] = useState('');
  const [lossOfUse, setLossOfUse] = useState('');
  const [liability, setLiability] = useState('300000');
  const [standardPerilsDeductible, setStandardPerilsDeductible] = useState('2500');
  const [windHailDeductible, setWindHailDeductible] = useState('2500');
  const [paymentMethod, setPaymentMethod] = useState('EFT');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [allowTextMessages, setAllowTextMessages] = useState('No');
  const [acceptElectronicDocuments, setAcceptElectronicDocuments] = useState('Yes');
  const yn = ['No', 'Yes'];
  const isYes = (v: string) => v === 'Yes';

  return (
    <div className="ca-run__grid">
      <Field label="First Name" value={firstName} onChange={setFirstName} />
      <Field label="Middle Name" value={middleName} onChange={setMiddleName} />
      <Field label="Last Name" value={lastName} onChange={setLastName} />
      <Field label="Date of Birth" value={dob} onChange={setDob} placeholder="MM/DD/YYYY" />
      <SelectField label="Property Type" value={propertyType} onChange={setPropertyType} options={['Main Residence', 'Secondary/Seasonal', 'Long Term Rental']} />
      <Field label="Property Address" value={address} onChange={setAddress} placeholder="Specific enough that Openly's first suggestion is the property" />
      <Field label="Roof Year Built" value={roofYearBuilt} onChange={setRoofYearBuilt} />
      <Field label="Square Footage" value={squareFootage} onChange={setSquareFootage} />
      <Field label="Year Built" value={yearBuilt} onChange={setYearBuilt} />
      <Field label="Bathrooms" value={bathrooms} onChange={setBathrooms} />
      <SelectField label="Stories" value={stories} onChange={setStories} options={['1', '1.5', '2', '2.5', '3', '4+']} />
      <Field label="Foundation Type" value={foundationType} onChange={setFoundationType} placeholder="As Openly lists it" />
      <Field label="Building Material" value={buildingMaterial} onChange={setBuildingMaterial} placeholder="As Openly lists it" />
      <Field label="Single Family Home?" value={singleFamilyHome} onChange={setSingleFamilyHome} placeholder="As Openly lists it" />
      <Field label="Unconventional Home Type" value={unconventionalHomeType} onChange={setUnconventionalHomeType} placeholder="As Openly lists it" />
      <Field label="Insurance Cancellation Status (3 yrs)" value={cancellationStatus} onChange={setCancellationStatus} placeholder="As Openly lists it" />
      <Field label="Home Insurance Status" value={homeInsuranceStatus} onChange={setHomeInsuranceStatus} placeholder="As Openly lists it" />
      <SelectField label="Garage" value={hasGarage} onChange={setHasGarage} options={yn} />
      <SelectField label="Swimming Pool" value={hasPool} onChange={setHasPool} options={yn} />
      <SelectField label="Solar Panels" value={hasSolarPanels} onChange={setHasSolarPanels} options={yn} />
      <SelectField label="Dogs" value={hasDogs} onChange={setHasDogs} options={yn} />
      <SelectField label="Stove Heating" value={stoveHeating} onChange={setStoveHeating} options={yn} />
      <SelectField label="Business On Site" value={onSiteBusiness} onChange={setOnSiteBusiness} options={yn} />
      <SelectField label="Rural Fire Dues" value={ruralFireDues} onChange={setRuralFireDues} options={yn} />
      <Field label="Other Structures" value={otherStructures} onChange={setOtherStructures} placeholder="$" />
      <Field label="Personal Property" value={personalProperty} onChange={setPersonalProperty} placeholder="$" />
      <Field label="Loss of Use" value={lossOfUse} onChange={setLossOfUse} placeholder="$" />
      <Field label="Liability" value={liability} onChange={setLiability} placeholder="$" />
      <Field label="Standard Perils Deductible" value={standardPerilsDeductible} onChange={setStandardPerilsDeductible} placeholder="$" />
      <Field label="Wind/Hail Deductible" value={windHailDeductible} onChange={setWindHailDeductible} placeholder="$" />
      <SelectField label="Payment Method at Bind" value={paymentMethod} onChange={setPaymentMethod} options={['EFT', 'CreditCard']} />
      <Field label="Phone Number" value={phoneNumber} onChange={setPhoneNumber} />
      <Field label="Email" value={email} onChange={setEmail} placeholder="Needed if electronic documents = Yes" />
      <SelectField label="Allow Text Messages" value={allowTextMessages} onChange={setAllowTextMessages} options={yn} />
      <SelectField label="Accept Electronic Documents" value={acceptElectronicDocuments} onChange={setAcceptElectronicDocuments} options={['Yes', 'No']} />
      <label className="ca-run__field ca-run__field--full">
        <span>
          <input type="checkbox" checked={disclosureRead} onChange={(e) => setDisclosureRead(e.target.checked)} />{' '}
          The applicable state disclosure was read to the applicant (Openly runs a real insurance-score check on them)
        </span>
      </label>
      <button
        type="button"
        className="ca-run__btn ca-run__btn--full"
        disabled={disabled || !disclosureRead}
        onClick={() => onSubmit({
          newQuote: { firstName, middleName: middleName || undefined, lastName, dob, propertyType, address, stateDisclosureRead: disclosureRead },
          property: {
            roofYearBuilt: Number(roofYearBuilt), squareFootage: Number(squareFootage), yearBuilt: Number(yearBuilt), bathrooms: Number(bathrooms), stories,
            foundationType, buildingMaterial, singleFamilyHome, unconventionalHomeType, cancellationStatus, homeInsuranceStatus,
            hasGarage: isYes(hasGarage), hasPool: isYes(hasPool), hasSolarPanels: isYes(hasSolarPanels), hasDogs: isYes(hasDogs),
            stoveHeating: isYes(stoveHeating), onSiteBusiness: isYes(onSiteBusiness), ruralFireDues: isYes(ruralFireDues),
          },
          coverage: {
            otherStructures: Number(otherStructures), personalProperty: Number(personalProperty), lossOfUse: Number(lossOfUse), liability: Number(liability),
            standardPerilsDeductible: Number(standardPerilsDeductible), windHailDeductible: Number(windHailDeductible),
          },
          billing: { paymentMethod },
          admin: { phoneNumber, email: email || undefined, allowTextMessages: isYes(allowTextMessages), acceptElectronicDocuments: isYes(acceptElectronicDocuments) },
        })}
      >
        Create Quote
      </button>
    </div>
  );
};

export const ProgressiveHomeQuoteForm: React.FC<QuoteFormProps> = ({ onSubmit, disabled }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('F');
  const [maritalStatus, setMaritalStatus] = useState('Single');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [addressSearchText, setAddressSearchText] = useState('');

  const [effectiveDate, setEffectiveDate] = useState('');
  const [yearBuilt, setYearBuilt] = useState('');
  const [livingAreaSqFt, setLivingAreaSqFt] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [yearRoofInstalled, setYearRoofInstalled] = useState('');
  const [stories, setStories] = useState<string>(HOME.stories[0]);
  const [construction, setConstruction] = useState<string>('Frame');
  const [exteriorWalls, setExteriorWalls] = useState<string>('Vinyl Siding');
  const [garage, setGarage] = useState<string>('None');
  const [roofMaterial, setRoofMaterial] = useState<string>('Shingles, Architectural');
  const [substructure, setSubstructure] = useState<string>('Slab-on-ground');
  const [fullBaths, setFullBaths] = useState('2');
  const [halfBaths, setHalfBaths] = useState('0');
  const [swimmingPool, setSwimmingPool] = useState<string>('None');
  const [heating, setHeating] = useState<string>('Gas Forced Air');
  const [dwellingCoverage, setDwellingCoverage] = useState('');

  const [priorInsurer, setPriorInsurer] = useState('');
  const [personalProperty, setPersonalProperty] = useState('50');
  const [liabilityLimit, setLiabilityLimit] = useState<string>('$300,000');
  const [deductible, setDeductible] = useState<string>('$1,000');

  const [disclosureProvided, setDisclosureProvided] = useState(false);
  const [eligibilityConfirmed, setEligibilityConfirmed] = useState(false);

  const missing = [
    [firstName, 'first name'], [lastName, 'last name'], [dateOfBirth, 'date of birth'], [phoneNumber, 'phone'],
    [addressSearchText, 'address'], [effectiveDate, 'effective date'], [yearBuilt, 'year built'], [livingAreaSqFt, 'living area'],
    [purchaseDate, 'purchase date'], [yearRoofInstalled, 'roof year'], [priorInsurer, 'current home insurer'],
  ].filter(([value]) => !value.trim()).map(([, label]) => label);
  const ready = missing.length === 0 && disclosureProvided && eligibilityConfirmed;

  return (
    <div className="ca-run__grid">
      <Field label="First Name" value={firstName} onChange={setFirstName} />
      <Field label="Last Name" value={lastName} onChange={setLastName} />
      <Field label="Date of Birth" value={dateOfBirth} onChange={setDateOfBirth} placeholder="MM/DD/YYYY" />
      <SelectField label="Gender" value={gender} onChange={setGender} options={['F', 'M']} />
      <SelectField label="Marital Status" value={maritalStatus} onChange={setMaritalStatus} options={['Single', 'Married', 'Separated', 'Widowed', 'Divorced']} />
      <Field label="Phone" value={phoneNumber} onChange={setPhoneNumber} placeholder="10 digits" />
      <Field label="Email (optional)" value={email} onChange={setEmail} />
      <Field label="Home Address (mailing and insured location)" value={addressSearchText} onChange={setAddressSearchText} placeholder="203 N Main St, Sand Springs, OK" />

      <Field label="Policy Effective Date" value={effectiveDate} onChange={setEffectiveDate} placeholder="MM/DD/YYYY" />
      <Field label="Year Built" value={yearBuilt} onChange={setYearBuilt} placeholder="2005" />
      <Field label="Living Area (sq ft, no basement)" value={livingAreaSqFt} onChange={setLivingAreaSqFt} placeholder="1800" />
      <Field label="Purchase Date" value={purchaseDate} onChange={setPurchaseDate} placeholder="MM/DD/YYYY" />
      <Field label="Year Roof Installed" value={yearRoofInstalled} onChange={setYearRoofInstalled} placeholder="2018" />
      <SelectField label="Stories" value={stories} onChange={setStories} options={[...HOME.stories]} />
      <SelectField label="Construction" value={construction} onChange={setConstruction} options={[...HOME.construction]} />
      <SelectField label="Exterior Walls" value={exteriorWalls} onChange={setExteriorWalls} options={[...HOME.exteriorWalls]} />
      <SelectField label="Garage" value={garage} onChange={setGarage} options={[...HOME.garage]} />
      <SelectField label="Roof Material" value={roofMaterial} onChange={setRoofMaterial} options={[...HOME.roofMaterial]} />
      <SelectField label="Foundation" value={substructure} onChange={setSubstructure} options={[...HOME.substructure]} />
      <SelectField label="Full Baths" value={fullBaths} onChange={setFullBaths} options={['1', '2', '3', '4', '5']} />
      <SelectField label="Half Baths" value={halfBaths} onChange={setHalfBaths} options={['0', '1', '2', '3']} />
      <SelectField label="Swimming Pool" value={swimmingPool} onChange={setSwimmingPool} options={[...HOME.swimmingPool]} />
      <SelectField label="Heat Source" value={heating} onChange={setHeating} options={[...HOME.heating]} />
      <Field label="Dwelling Coverage (optional)" value={dwellingCoverage} onChange={setDwellingCoverage} placeholder="Blank = Progressive's replacement cost" />

      <SelectField label="Current Home Insurer" value={priorInsurer} onChange={setPriorInsurer} options={['', ...HOME.priorInsurer]} />
      <SelectField label="Personal Property (% of dwelling)" value={personalProperty} onChange={setPersonalProperty} options={['0', '25', '50', '70']} />
      <SelectField label="Liability Limit" value={liabilityLimit} onChange={setLiabilityLimit} options={[...HOME.liabilityLimit]} />
      <SelectField label="All Other Perils Deductible" value={deductible} onChange={setDeductible} options={[...HOME.allOtherPerilsDeductible]} />

      <label className="ca-run__checkbox">
        <input type="checkbox" checked={disclosureProvided} onChange={(e) => setDisclosureProvided(e.target.checked)} />
        <span>
          I read or gave the customer Progressive&apos;s disclosure: &ldquo;Like most insurance companies, Progressive uses information from you and other
          sources, such as your driving, claims and credit histories, to calculate an accurate price for your insurance.&rdquo;
        </span>
      </label>
      <label className="ca-run__checkbox">
        <input type="checkbox" checked={eligibilityConfirmed} onChange={(e) => setEligibilityConfirmed(e.target.checked)} />
        <span>I have verified that none of these apply to this home: {HOME_INELIGIBLE_CONDITIONS}.</span>
      </label>

      <button
        type="button"
        className="ca-run__btn ca-run__btn--full"
        disabled={disabled || !ready}
        title={ready ? undefined : missing.length > 0 ? `Still needed: ${missing.join(', ')}` : 'Tick both confirmations to continue'}
        onClick={() => onSubmit({
          applicant: { firstName, lastName, dateOfBirth, gender, phoneNumber: phoneNumber.replace(/\D/g, ''), email: email || undefined, addressSearchText, maritalStatus, disclosureProvided },
          property: {
            effectiveDate, eligibilityConfirmed, yearBuilt, livingAreaSqFt, purchaseDate, yearRoofInstalled, stories, construction, exteriorWalls,
            garage, roofMaterial, substructure, fullBaths, halfBaths, swimmingPool, heating,
            dwellingCoverage: dwellingCoverage.trim() ? Number(dwellingCoverage.replace(/[^\d]/g, '')) : undefined,
          },
          details: { priorInsurer, personalPropertyPercent: Number(personalProperty), liabilityLimit, allOtherPerilsDeductible: deductible },
        })}
      >
        Create Home Quote
      </button>
    </div>
  );
};
